import './index.scss'

import { bookingIdToDiseaseId } from 'constants/diseases'

import { useState, useEffect } from 'react'
import T from 'prop-types'
import { get, set, findIndex, unset, forEach } from 'lodash'
import BEMHelper from 'react-bem-helper'
import Container from 'components/Container'
import Transition from 'components/Transition'
import Message from 'components/Message'
import Button from 'components/Button'
import Spinner from 'components/Spinner'
import Editorial from 'components/Editorial'
import { getBookingType } from 'data/booking'
import scrollTo from 'lib/scrollTo'
import { Route, useHistory, Redirect, useLocation } from 'react-router-dom'
import useOrgUnit from 'hooks/useOrgUnit'
import Joi from 'joi'
import { getJSON, setJSON } from 'lib/storage'
import Header from 'apps/booking/components/Header'

import { getVaccinationElegibility, getVaccinations } from '../../../lib/requests'
import { filterActiveVaccinations } from '../../../lib/filterBookingVaccinations'
import useSession from '../../../hooks/useSession'
import Indicators from '../components/Indicators'
import SelectPatient, { validator as selectPatientValidator } from './steps/SelectPatient'
import RiskFactors, { validator as riskFactorsValidator } from './steps/RiskFactors'
import Why, { validator as whyValidator } from './steps/Why'
import Additional, { validator as additionalValidator } from './steps/Additional'
import Where, { validator as whereValidator } from './steps/Where'
import When, { validator as whenValidator } from './steps/When'
import Symptoms from './steps/Symptoms'
import Allergy, { validator as allergyValidator } from './steps/Allergy'
import ClinicalInformation, {
  validator as criticalInformationValidator,
} from './steps/ClinicalInformation'
import WomanQuestions, {
  fertilePredicate,
  validator as womanQuestionsValidator,
} from './steps/WomanQuestions'
import ContactInformation, {
  validator as contactInformationValidator,
} from './steps/ContactInformation'
import ControlQuestion, {
  validator as controlQuestionValidator,
  controlQuestionPredicate,
} from './steps/ControlQuestion'
import Vaccinations, {
  validator as VaccinationsValidator,
  vaccinationsPredicate,
} from './steps/Vaccinations'
import RiskGroupA, {
  validator as riskGroupAValidator,
  riskGroupAPredicate,
} from './steps/RiskGroupA'
import RiskGroupB, {
  validator as riskGroupBValidator,
  riskGroupBPredicate,
} from './steps/RiskGroupB'
import Rejection, { validator as rejectionValidator, rejectionPredicate } from './steps/Rejection'

const bem = new BEMHelper('wizard')

/**
 * An optional showPredicate property can be set on the step. If a predicate is provided
 * it will be called with the form data as the first argument, user_id (national id of
 * the signed in user as the second argument and the vaccination id as the third argument
 * ). The step will be shown if no predicate is specified, or if the predicate yields a truthy value
 */
const AVAILABLE_STEPS = {
  COVID_TEST: [
    {
      key: 'who',
      slug: 'hvem',
      component: SelectPatient,
      validator: selectPatientValidator,
    },
    { key: 'why', slug: 'hvorfor', component: Why, validator: whyValidator },
    {
      key: 'risk_factors',
      slug: 'risikogruppe',
      component: RiskFactors,
      validator: riskFactorsValidator,
    },
    {
      key: 'contact',
      slug: 'kontaktinfo',
      component: Additional,
      validator: additionalValidator,
    },
    { key: 'where', slug: 'sted', component: Where, validator: whereValidator },
    { key: 'when', slug: 'tid', component: When, validator: whenValidator },
  ],
  FLU_VACCINE: [
    {
      key: 'who',
      slug: 'hvem',
      component: SelectPatient,
      validator: selectPatientValidator,
    },
    {
      key: 'symptoms',
      slug: 'symptomer',
      component: Symptoms,
      validator: () => Joi.any(),
    },
    {
      key: 'allergy',
      slug: 'allergi',
      component: Allergy,
      validator: allergyValidator,
    },
    {
      key: 'contact',
      slug: 'kontaktinfo',
      component: ContactInformation,
      validator: contactInformationValidator,
      showPredicate: (form) => !form?.vaccinationId,
    },
    { key: 'where', slug: 'sted', component: Where, validator: whereValidator },
    { key: 'when', slug: 'tid', component: When, validator: whenValidator },
  ],
  COVID_VACCINE: [
    {
      key: 'who',
      slug: 'hvem',
      component: SelectPatient,
      validator: selectPatientValidator,
    },

    // Show to everone
    {
      key: 'vaccinations',
      slug: 'vaksinasjoner',
      component: Vaccinations,
      validator: VaccinationsValidator,
      showPredicate: vaccinationsPredicate, // Show only if user has registered vaccinations / bookings
    },

    // Show to everone
    {
      key: 'control_question',
      slug: 'bekreftelse',
      component: ControlQuestion,
      validator: controlQuestionValidator,
      showPredicate: controlQuestionPredicate,
    },

    // Show only to non eligible
    {
      key: 'not_eligible',
      slug: 'ikke-prioritert-gruppe',
      component: Rejection,
      validator: rejectionValidator,
      showPredicate: rejectionPredicate,
    },

    // Show only to waitinglist
    {
      key: 'riskGroupA',
      slug: 'risikogruppe-a',
      component: RiskGroupA,
      validator: riskGroupAValidator,
      showPredicate: riskGroupAPredicate,
    },

    // Show only to waitinglist
    {
      key: 'riskGroupB',
      slug: 'risikogruppe-b',
      component: RiskGroupB,
      validator: riskGroupBValidator,
      showPredicate: riskGroupBPredicate,
    },

    // Show to everyone
    {
      key: 'clinical_information',
      slug: 'kliniske-opplysninger',
      component: ClinicalInformation,
      validator: criticalInformationValidator,
    },

    // Show only to women
    {
      key: 'woman_questions',
      slug: 'kvinne',
      component: WomanQuestions,
      validator: womanQuestionsValidator,
      showPredicate: (form, userId) => {
        if (form?.vaccinationId) {
          return false
        }

        return fertilePredicate(form, userId)
      },
    },

    // Show to everyone
    {
      key: 'contact',
      slug: 'kontaktinfo',
      component: ContactInformation,
      validator: contactInformationValidator,
      showPredicate: (form, userId) => !form?.vaccinationId,
    },

    // Show only to eligible (and health personnel)
    {
      key: 'where',
      slug: 'sted',
      component: Where,
      validator: whereValidator,
      showPredicate: (form, userId) => !form?.vaccinationId,
    },

    // Show only to eligible (and health personnel)
    {
      key: 'when',
      slug: 'tid',
      component: When,
      validator: whenValidator,
      showPredicate: (form, userId) => !form?.vaccinationId,
    },
  ],
}

export default function Wizard({ baseUrl }) {
  const {
    session: { user_id },
    clearSession,
  } = useSession()

  // Set up form state, pull from session storage if set
  const [form, setForm] = useState(getJSON('bookingState') ?? {})
  const [backwards, setBackwards] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  // Show error if there is an error loading vaccinations or eligibility
  const [isBookingDataError, setIsBookingDataError] = useState(false)

  const { push } = useHistory()
  const { pathname } = useLocation()

  const [, orgUnitSlug, bookingSlug = 'koronatest', pathStep] = pathname.match(
    /^\/([^/]+)(?:\/([^/]+))(?:\/([^/]+))?/,
  )

  const { ou: orgUnit, isLoading: ouLoading } = useOrgUnit(orgUnitSlug)

  const bookingType = getBookingType(bookingSlug)
  const bookingTypeId = bookingType?.id

  const STEPS = AVAILABLE_STEPS[get(bookingType, 'id', 'COVID_TEST') || 'COVID_TEST'].filter(
    ({ showPredicate }) => !showPredicate || showPredicate(form, user_id),
  )

  // Special logic for vaccines (covid and flu)
  useEffect(() => {
    if (!orgUnit || ouLoading) {
      return
    }

    if (bookingTypeId === 'COVID_TEST') {
      setIsLoading(false)
      return
    }

    const diseaseId = bookingIdToDiseaseId(bookingTypeId)

    // Check if user is eligible for a vaccine right away
    async function checkElegibility() {
      const { data } = await getVaccinationElegibility({ ouId: orgUnit.id, diseaseId })
      handleUpdate('isEligible', data?.eligible || false)
      handleUpdate('priority', data?.priority)
    }

    // Save eligible priority groups
    handleUpdate(
      'eligiblePriorityGroups',
      orgUnit?.vaccination_properties?.[diseaseId]?.eligible_priorities,
    )

    // Save org unit title to session
    handleUpdate('orgUnitTitle', orgUnit?.title)

    // Check if user has registered vaccinations
    async function getUserVaccinations() {
      const { data } = await getVaccinations({ ouId: orgUnit.id, diseaseId })

      // Only keep vaccinations with an actual booking or an actual vaccination
      const filteredVaccinations = data?.filter(
        (vaccination) => vaccination.start_time || vaccination.completed_at,
      )
      handleUpdate('registeredVaccinations', filteredVaccinations)
    }

    async function getInitialVaccinationData() {
      await Promise.all([getUserVaccinations(), checkElegibility()]).catch((e) => {
        console.warn(e)
        // Show error page instead of normal booking flow if something goes wrong
        setIsBookingDataError(true)
      })
      setIsLoading(false)
    }

    getInitialVaccinationData()
  }, [user_id, push, orgUnit, bookingTypeId, ouLoading])

  // Validate that the user has filled out all required info up to the current step, otherwise redirect
  useEffect(() => {
    // Get current step index
    const currentStepIndex = findIndex(STEPS, ['slug', pathStep])

    let lastValidStepIndex = -1

    // Get id of last valid step up and to the current step
    forEach(STEPS, (step, index) => {
      // If last step was not valid, user is not allowed to go to this step either
      if (index > lastValidStepIndex + 1) {
        return
      }

      // If this step has no validator, it is per definition valid
      if (!step.validator) {
        lastValidStepIndex = index
        return
      }

      // Validate data using validtor
      const { error } = step.validator(form, user_id, form?.vaccinationId).validate(form)
      if (error) {
        return
      }

      // Validation passed, set last valid step to this step index and go on
      lastValidStepIndex = index
    })

    // If the current step is the one after the last valid, or before that, all is good
    if (currentStepIndex <= lastValidStepIndex + 1) {
      return
    }

    // User has jumped into the middle of the wizard. Redirect back to the one after the last valid one
    console.error("The step didn't validate")
    const redirectStepIndex = Math.min(STEPS.length - 1, lastValidStepIndex + 1)
    push({
      pathname: `${baseUrl}/${STEPS[redirectStepIndex].slug}`,
      state: { noScroll: true },
    })
  }, [form, orgUnit, pathStep, STEPS, baseUrl, push, user_id, form?.vaccinationId])

  const handleUpdate = (key, value) => {
    setForm((form) => {
      const nextState = set({ ...form }, key, value)

      if (value === undefined) {
        unset(nextState, key)
      }

      // Set updated form state to session storage
      setJSON('bookingState', nextState)

      return nextState
    })
  }

  const changeStep =
    (value, back = false) =>
    () => {
      setBackwards(back)

      push({
        pathname: `${baseUrl}/${value}`,
        state: { noScroll: true },
      })

      scrollTo({ to: 0, duration: 300 })
    }

  if (isLoading) {
    return <Spinner full center box={false} />
  }

  // Hospitals
  // Show error page if they have no active vaccinations, since they should not be allowed to book themselves
  // It would be better to allow them to see their vaccinations in step 1, but it seemed impossible to get orgUnit.isHospital into validator()...
  if (orgUnit?.isHospital && bookingType.id === 'COVID_VACCINE') {
    const hasActiveVaccinations = !!filterActiveVaccinations(form?.registeredVaccinations)?.length

    if (!hasActiveVaccinations) {
      return (
        <>
          <Header />
          <Container size="tight">
            <Editorial>
              <h1>Siden kan ikke vises</h1>
              <p>
                Dette skyldes trolig at du bruker en ugyldig lenke eller ikke har blitt satt opp til
                vaksinasjon.
              </p>

              <h2>Gjør dette:</h2>
              <p>
                <strong>1) Logg først ut ved å trykke på knappen under.</strong>
              </p>
              <p>
                <strong>2) Trykk så på lenken du har mottatt via SMS.</strong>
              </p>

              <p>
                Dersom feilen vedvarer må du gi beskjed til vaksineringsansvarlig ved din avdeling
                eller sykehus.
              </p>
              <Button onClick={() => clearSession()} primary>
                Logg ut
              </Button>
            </Editorial>
          </Container>
        </>
      )
    }
  }

  // Show error page if bookingtype or org unit is missing, and also if there is errors loading vaccinations
  if (!bookingType || !orgUnit || isBookingDataError) {
    return (
      <>
        <Header />
        <Container size="tight">
          <h1>Det skjedde en feil</h1>
          {isBookingDataError && (
            <Message type="warning">Vi kunne ikke laste inn dine vaksinasjonsdata.</Message>
          )}
          <p>Vennligst gå tilbake til start og prøv igjen.</p>
          <Button small arrow to={`/${orgUnit.slug}`}>
            Gå til start
          </Button>
          <p>
            Hvis problemet vedvarer kan du prøve å logge ut, og så logge inn igjen om en liten
            stund.
          </p>
          <Button onClick={() => clearSession()} small arrow>
            Avslutt og logg ut
          </Button>
        </Container>
      </>
    )
  }

  return (
    <>
      <Header title={get(bookingType, 'title', '')}>
        <Indicators steps={STEPS} step={pathStep} changeStep={changeStep} form={form} />
      </Header>

      <div {...bem('', { backwards })}>
        <Container size="wizard" node="main">
          <Route exact path={baseUrl}>
            <Redirect to={`${baseUrl}/${STEPS[0].slug}`} />
          </Route>

          <Route
            exact
            path={`${baseUrl}/:step`}
            render={({
              match: {
                params: { step = 0 },
              },
            }) => {
              const currentStepIndex = findIndex(STEPS, { slug: step })
              const currentStep = STEPS[currentStepIndex]
              const nextStep = STEPS[currentStepIndex + 1]

              const StepComponent = currentStep?.component

              // This shouldn't happen, but could indicate a bad url. Redirect to first step
              if (!StepComponent) {
                return <Redirect to={`${baseUrl}/${STEPS[0].slug}`} />
              }

              return (
                <div {...bem('content')}>
                  <Transition updateKey={step} {...bem('transition')}>
                    {currentStep && (
                      <StepComponent
                        key={currentStep.key}
                        changeStep={get(nextStep, 'slug') ? changeStep(nextStep.slug) : null}
                        values={form}
                        orgUnit={orgUnit}
                        bookingType={bookingType}
                        onChange={handleUpdate}
                      />
                    )}
                  </Transition>
                </div>
              )
            }}
          />
        </Container>
      </div>
    </>
  )
}

Wizard.propTypes = {
  baseUrl: T.string.isRequired,
}
